import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cake, Calendar, ArrowRight, Sparkles, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { EventCard } from "@/components/EventCard";
import { BornTodayShareCard } from "@/components/BornTodayShareCard";
import { BirthdayCaptureForm } from "@/components/BirthdayCaptureModal";
import { FamousBirthdays } from "@/components/FamousBirthdays";
import { UpcomingObservances } from "@/components/UpcomingObservances";
import { LifeInNumberOnes } from "@/components/LifeInNumberOnes";
import { SportsBirthYear } from "@/components/Sports";
import { MoviesBirthYear, MoviesBirthdayTwins } from "@/components/Movies";
import { useAuth } from "@/context/AuthContext";
import { cleanEvents, personName, MONTHS, ddmmLabel, setSeo } from "@/lib/events";

const selectClass = "px-3 py-2.5 rounded-xl bg-white border border-[#e5e7eb] text-ink focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/40 text-sm font-medium";
const box = "bg-white border border-[#e5e7eb] rounded-2xl p-5";
const eyebrow = "text-[11px] font-bold uppercase tracking-[0.15em] text-gold";

const todayKey = () => { const t = new Date(); return `${String(t.getDate()).padStart(2, "0")}-${String(t.getMonth() + 1).padStart(2, "0")}`; };

const SearchAnotherDate = ({ ddmm, onSearch }) => {
  const [d, m] = ddmm.split("-").map(Number);
  const [day, setDay] = useState(d);
  const [month, setMonth] = useState(m);
  const daysInMonth = new Date(2024, month, 0).getDate();
  const go = () => onSearch(`${String(Math.min(day, daysInMonth)).padStart(2, "0")}-${String(month).padStart(2, "0")}`);
  return (
    <div className={box} data-testid="birthdays-search-box">
      <p className={`${eyebrow} inline-flex items-center gap-1.5`}><Calendar className="w-3.5 h-3.5" /> Search another date</p>
      <h2 className="mt-1 text-xl font-bold text-ink">Who was born on any day of the year?</h2>
      <p className="mt-1 text-[13px] text-ink-muted">Pick a day and month to see every birthday we hold for that date.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-muted">Day</span>
          <select data-testid="birthdays-search-day" value={Math.min(day, daysInMonth)} onChange={(e) => setDay(Number(e.target.value))} className={selectClass}>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-muted">Month</span>
          <select data-testid="birthdays-search-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className={selectClass}>
            {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
        </label>
        <button type="button" data-testid="birthdays-search-submit" onClick={go} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-ink text-white font-semibold text-sm hover:bg-gold hover:text-ink transition-colors">
          Show birthdays <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {ddmm !== todayKey() && (
        <button type="button" data-testid="birthdays-search-today" onClick={() => onSearch(todayKey())} className="mt-3 text-[12px] font-semibold text-gold hover:underline">Back to today's birthdays</button>
      )}
    </div>
  );
};

const MyBirthday = ({ data, first }) => (
  <div data-testid="birthdays-mine">
    <p className={`${eyebrow} inline-flex items-center gap-1.5`}><Sparkles className="w-3.5 h-3.5" /> Your birthday</p>
    <h2 className="mt-1 text-xl font-bold text-ink">{data.days_until === 0 ? `Happy birthday${first ? `, ${first}` : ""}!` : `${data.days_until} day${data.days_until === 1 ? "" : "s"} to go`}</h2>
    <p className="mt-1 text-[13px] text-ink-muted" data-testid="birthdays-summary">Born {data.date_label} · {data.sign_name} {data.glyph}{"\uFE0E"} ({data.dates})</p>
    <p className="mt-3 bg-paper border-l-4 border-gold rounded-r-xl px-4 py-3 text-[14px] leading-[1.7] text-[#333]">{data.horoscope}</p>
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
      <Link to={`/birthdays?date=${data.date_dd_mm}`} data-testid="birthdays-my-twins-link" className="text-[12px] font-semibold text-gold hover:underline">My {data.twins.length} birthday twin{data.twins.length === 1 ? "" : "s"} →</Link>
      <Link to={`/horoscope#${data.sign}`} className="text-[12px] font-semibold text-gold hover:underline">Full {data.sign_name} profile →</Link>
    </div>
  </div>
);

export default function Birthdays() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const ddmm = /^\d{2}-\d{2}$/.test(params.get("date") || "") ? params.get("date") : todayKey();
  const [births, setBirths] = useState(null);
  const [wiki, setWiki] = useState([]);
  const [mine, setMine] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const isToday = ddmm === todayKey();
  const dateLabel = ddmmLabel(ddmm);

  useEffect(() => {
    if (window.location.hash === "#twins") setTimeout(() => document.getElementById("twins")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, []);

  useEffect(() => {
    if (!user) { setMine(null); return; }
    api.get("/me/twins").then((r) => setMine(r.data)).catch(() => setMine(null));
  }, [user]);

  useEffect(() => {
    setBirths(null); setWiki([]); setExpanded(false);
    api.get(`/coincidences/${ddmm}`).then(async (r) => {
      const rows = cleanEvents(r.data.births).sort((a, b) => Number(a.year) - Number(b.year));
      const ours = new Set(rows.map((e) => personName(e.event_title).toLowerCase()));
      const plain = (t) => (t || "").replace(/\s*\(.*\)\s*$/, "");
      setWiki((r.data.wiki_births || []).filter((w) => !ours.has(plain(w.page?.title).toLowerCase())).map((w) => ({ ...w, name: plain(w.page?.title) || (w.text || "").split(",")[0] })));
      setBirths(rows);
      const names = rows.slice(0, 24).map((e) => personName(e.event_title));
      if (!names.length) return;
      try {
        const pr = await api.get("/portraits", { params: { names: names.join("|") } });
        setBirths(rows.map((e) => ({ ...e, portrait: pr.data.portraits[personName(e.event_title)]?.image || null })));
      } catch { /* keep category art */ }
    }).catch(() => setBirths([]));
  }, [ddmm]);

  useEffect(() => {
    const names = [...(births || []).map((e) => personName(e.event_title)), ...wiki.map((w) => w.name)].filter(Boolean).slice(0, 3).join(", ");
    const total = (births?.length || 0) + wiki.length;
    setSeo({
      title: `Famous Birthdays on ${dateLabel} — who was born on this day | OddlyTrue`,
      description: `${total || "Famous"} people born on ${dateLabel}${names ? `, including ${names}` : ""}. Find your birthday twins, star sign and the No.1 song from the day you were born.`,
      canonical: `https://oddlytrue.co/birthdays${isToday ? "" : `?date=${ddmm}`}`,
    });
  }, [births, wiki, dateLabel, ddmm, isToday]);

  const INITIAL = 12;
  const shown = useMemo(() => (expanded ? births || [] : (births || []).slice(0, INITIAL)), [births, expanded]);
  const cardPeople = useMemo(() => [...(births || []).map((e) => ({ name: personName(e.event_title), year: e.year })), ...wiki.map((w) => ({ name: w.name, year: w.year }))], [births, wiki]);
  const totalPeople = (births?.length || 0) + wiki.length;
  const first = (user?.name || "").split(" ")[0];
  const search = (key) => { setParams(key === todayKey() ? {} : { date: key }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="birthdays-page">
        <AdSlot id="adsense-birthdays" slot="BIRTHDAYS" size="leaderboard" className="mb-8" />
        <div className="flex items-center gap-2">
          <Cake className="w-4 h-4 text-gold" />
          <p className={eyebrow}>Birthdays</p>
        </div>
        <h1 data-testid="birthdays-title" className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink leading-tight">Born on {isToday ? "this day" : dateLabel}</h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">
          {isToday ? `Today is ${dateLabel}. ` : ""}Every famous birthday in the OddlyTrue archive for {dateLabel} — from royalty and rebels to pop stars and pioneers — plus a quick way to find who shares <em>your</em> day.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div id="twins" className={`${box} scroll-mt-28`} data-testid="birthdays-twins-box">
            {mine?.has_dob ? <MyBirthday data={mine} first={first} /> : <BirthdayCaptureForm source="birthdays_page" doneLabel="Start again" />}
          </div>
          <SearchAnotherDate ddmm={ddmm} onSearch={search} />
        </div>

        <section id="born" className="mt-10" data-testid="birthdays-list-section">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Cake className="w-5 h-5 text-gold" />
              <h2 className="text-2xl font-bold text-ink">{isToday ? "Born today" : `Born on ${dateLabel}`}</h2>
              {births && <span data-testid="birthdays-count" className="ml-1 text-[12px] font-semibold text-ink-muted">{totalPeople} {totalPeople === 1 ? "person" : "people"}</span>}
            </div>
            <Link to={`/?date=${ddmm}#today-in-history`} data-testid="birthdays-day-link" className="text-[13px] font-semibold text-gold hover:underline">Everything that happened on {dateLabel} →</Link>
          </div>
          {!births ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-white border border-[#e5e7eb] animate-pulse" />)}</div>
          ) : births.length === 0 && wiki.length === 0 ? (
            <p data-testid="birthdays-empty" className="text-sm text-ink-muted">No famous births on record for {dateLabel} yet — you might be the first.</p>
          ) : (
            <>
              {births.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="birthdays-grid">
                  {shown.map((e, i) => <EventCard key={e.id} e={e} i={i} total={births.length} />)}
                </div>
              )}
              {births.length > INITIAL && (
                <button type="button" data-testid="birthdays-toggle" onClick={() => setExpanded(!expanded)} className="mt-4 inline-flex items-center px-5 h-10 rounded-full border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white transition-colors">
                  {expanded ? "Show fewer" : `Show all ${births.length} birthdays`}
                </button>
              )}
              {wiki.length > 0 && (
                <div className={births.length ? "mt-8" : ""} data-testid="birthdays-wiki-section">
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink-muted mb-3">{births.length ? "More births on this day" : "Births on this day"} <span className="normal-case tracking-normal font-normal">· from Wikipedia</span></p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="birthdays-wiki">
                    {wiki.map((w, i) => (
                      <li key={w.url || i} data-testid={`birthday-wiki-${i + 1}`} className="flex gap-3 bg-white border border-[#e5e7eb] rounded-xl p-3">
                        {w.page?.thumbnail ? <img src={w.page.thumbnail} alt="" loading="lazy" className="w-12 h-12 rounded-full object-cover object-top shrink-0 ring-1 ring-ink/10" /> : <span className="w-12 h-12 rounded-full bg-paper shrink-0 inline-flex items-center justify-center text-ink-muted"><Cake className="w-5 h-5" /></span>}
                        <div className="min-w-0">
                          <p className="text-[12px] font-bold text-gold leading-none">{w.year}</p>
                          <p className="mt-1 text-[13px] text-ink leading-snug line-clamp-3">{w.text}</p>
                          {w.url && <a href={w.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-ink-muted hover:text-gold"><ExternalLink className="w-3 h-3" /> Wikipedia</a>}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-ink-muted">Wikipedia entries are reproduced under CC BY-SA 4.0 and link to the original article.</p>
                </div>
              )}
              <BornTodayShareCard ddmm={ddmm} dateLabel={dateLabel} people={cardPeople} />
            </>
          )}
        </section>

        <AdSlot id="adsense-birthdays-mid" slot="MID" size="rect300" className="my-8" />

        {mine?.has_dob && (
          <section className="mt-6" data-testid="birthdays-almanac">
            <p className={eyebrow}>Your birthday almanac</p>
            <LifeInNumberOnes items={mine.number_ones} className="mt-3" />
            <SportsBirthYear items={mine.sports_birth_year} year={mine.dob ? Number(String(mine.dob).slice(0, 4)) : null} className="mt-4" />
            <MoviesBirthYear data={mine.movies_birth_year} className="mt-4" />
            <MoviesBirthdayTwins data={mine.movies_birthday} className="mt-4" />
          </section>
        )}

        <FamousBirthdays ddmm={ddmm} hideToday />
        <UpcomingObservances className="mt-10" />
      </main>
      <Footer />
    </div>
  );
}
